import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Calendar, Plus, Edit2, Trash2, Users, Clock, MapPin, 
  ChevronDown, ChevronUp, Check, X, Mail, Phone, ArrowLeft, RotateCcw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminClasses = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [classes, setClasses] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [privateLessons, setPrivateLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tab');
    return ['classes', 'registrations', 'private'].includes(t) ? t : 'classes';
  });
  const [showClassModal, setShowClassModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [expandedClass, setExpandedClass] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  // Deep-link: scroll to the tabs section when the URL hash is #admin-tabs
  useEffect(() => {
    if (location.hash === '#admin-tabs' && !loading) {
      const el = document.getElementById('admin-tabs');
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }
  }, [location.hash, loading]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Basic ${localStorage.getItem('admin_token') || sessionStorage.getItem('admin_auth')}` }
  });

  const fetchData = async () => {
    try {
      const [classesRes, registrationsRes, privateLessonsRes] = await Promise.all([
        axios.get(`${API}/api/admin/classes`, getAuthHeaders()),
        axios.get(`${API}/api/admin/registrations`, getAuthHeaders()),
        axios.get(`${API}/api/admin/private-lessons`, getAuthHeaders())
      ]);
      setClasses(classesRes.data);
      setRegistrations(registrationsRes.data);
      setPrivateLessons(privateLessonsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveClass = async (classData) => {
    try {
      if (editingClass) {
        await axios.put(`${API}/api/admin/classes/${editingClass.id}`, classData, getAuthHeaders());
        toast.success('Class updated successfully');
      } else {
        await axios.post(`${API}/api/admin/classes`, classData, getAuthHeaders());
        toast.success('Class created successfully');
      }
      fetchData();
      setShowClassModal(false);
      setEditingClass(null);
    } catch (error) {
      toast.error('Failed to save class');
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Are you sure you want to delete this class?')) return;
    try {
      await axios.delete(`${API}/api/admin/classes/${classId}`, getAuthHeaders());
      toast.success('Class deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const handleUpdateRegistrationStatus = async (regId, status) => {
    try {
      await axios.put(`${API}/api/admin/registrations/${regId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleRefundRegistration = async (reg) => {
    const name = `${reg.first_name || ''} ${reg.last_name || ''}`.trim() || reg.email;
    if (!window.confirm(
      `Refund this registration?\n\n` +
      `${name} — ${reg.email}\n` +
      `Membership: ${(reg.membership_type || '').replace('_', ' ')}\n\n` +
      `This will refund the Stripe charge, cancel the spot, and email both you and the student.`
    )) return;
    const promise = axios.post(
      `${API}/api/admin/registrations/${reg.id}/refund`,
      {},
      getAuthHeaders()
    );
    toast.promise(promise, {
      loading: 'Processing refund…',
      success: (res) => {
        fetchData();
        return res.data?.already_refunded
          ? 'Already refunded — no action needed'
          : `Refund processed${res.data?.stripe_refund_id ? ` (${res.data.stripe_refund_id})` : ''}`;
      },
      error: (err) => err?.response?.data?.detail || 'Refund failed',
    });
  };

  const handleUpdatePrivateLessonStatus = async (requestId, status) => {
    try {
      await axios.put(`${API}/api/admin/private-lessons/${requestId}/status?status=${status}`, {}, getAuthHeaders());
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getClassRegistrations = (classId) => {
    return registrations.filter(r => r.class_id === classId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-white">
      {/* Header */}
      <div className="bg-stone-900 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold">Class Management</h1>
                <p className="text-sm text-stone-400">Manage classes, registrations & private lessons</p>
              </div>
            </div>
            {activeTab === 'classes' && (
              <button
                onClick={() => { setEditingClass(null); setShowClassModal(true); }}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-stone-900 rounded-lg font-bold hover:bg-amber-400 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Class
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div id="admin-tabs" className="max-w-7xl mx-auto px-4 py-6 scroll-mt-24">
        <div className="flex gap-2 mb-6">
          {[
            { id: 'classes', label: 'Classes', count: classes.length },
            { id: 'registrations', label: 'Registrations', count: registrations.length },
            { id: 'private', label: 'Private Lesson Requests', count: privateLessons.length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-amber-500 text-stone-900'
                  : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Classes Tab */}
        {activeTab === 'classes' && (
          <div className="space-y-4">
            {classes.length === 0 ? (
              <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                <Calendar className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400 mb-4">No classes created yet</p>
                <button
                  onClick={() => { setEditingClass(null); setShowClassModal(true); }}
                  className="px-4 py-2 bg-amber-500 text-stone-900 rounded-lg font-bold hover:bg-amber-400"
                >
                  Create Your First Class
                </button>
              </div>
            ) : (
              classes.map(danceClass => (
                <div key={danceClass.id} className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
                  <div 
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-stone-800/50"
                    onClick={() => setExpandedClass(expandedClass === danceClass.id ? null : danceClass.id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${danceClass.is_active ? 'bg-green-500' : 'bg-stone-600'}`} />
                      <div>
                        <h3 className="font-bold">{danceClass.name}</h3>
                        <p className="text-sm text-stone-400">
                          {danceClass.day_of_week} • {danceClass.start_time} - {danceClass.end_time}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        danceClass.category === 'kids' ? 'bg-purple-500/20 text-purple-400' :
                        danceClass.category === 'private' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-amber-500/20 text-amber-400'
                      }`}>
                        {danceClass.category}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs ${
                        danceClass.level === 'beginner' ? 'bg-green-500/20 text-green-400' :
                        danceClass.level === 'advanced' ? 'bg-red-500/20 text-red-400' :
                        'bg-stone-700 text-stone-300'
                      }`}>
                        {danceClass.level}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-stone-400">
                        {getClassRegistrations(danceClass.id).length} registrations
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingClass(danceClass); setShowClassModal(true); }}
                        className="p-2 hover:bg-stone-700 rounded-lg transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteClass(danceClass.id); }}
                        className="p-2 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      {expandedClass === danceClass.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </div>
                  </div>
                  
                  {expandedClass === danceClass.id && (
                    <div className="border-t border-stone-800 p-4 bg-stone-800/30">
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2 text-sm">
                          <p><span className="text-stone-400">Location:</span> {danceClass.location}</p>
                          <p><span className="text-stone-400">Instructor:</span> {danceClass.instructor}</p>
                          {danceClass.description && <p><span className="text-stone-400">Description:</span> {danceClass.description}</p>}
                        </div>
                        <div className="space-y-2 text-sm">
                          {danceClass.is_free ? (
                            <p><span className="text-green-400 font-bold">FREE CLASS</span></p>
                          ) : (
                            <>
                              <p><span className="text-stone-400">Per Class:</span> ${danceClass.price_per_class}</p>
                              <p><span className="text-stone-400">Monthly:</span> ${danceClass.price_monthly}</p>
                              <p><span className="text-stone-400">Yearly:</span> ${danceClass.price_yearly}</p>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Registrations for this class */}
                      <div className="mt-4">
                        <h4 className="font-bold text-sm mb-2">Registered Students ({getClassRegistrations(danceClass.id).length})</h4>
                        {getClassRegistrations(danceClass.id).length === 0 ? (
                          <p className="text-sm text-stone-500">No registrations yet</p>
                        ) : (
                          <div className="space-y-2 max-h-60 overflow-y-auto">
                            {getClassRegistrations(danceClass.id).map(reg => (
                              <div key={reg.id} className="flex items-center justify-between p-2 bg-stone-900 rounded-lg text-sm">
                                <div>
                                  <p className="font-medium">{reg.first_name} {reg.last_name}</p>
                                  <p className="text-stone-400">{reg.email}</p>
                                </div>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  reg.status === 'active' ? 'bg-green-500/20 text-green-400' :
                                  reg.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                                  'bg-stone-700 text-stone-300'
                                }`}>
                                  {reg.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Registrations Tab */}
        {activeTab === 'registrations' && (
          <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
            {registrations.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400">No registrations yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-stone-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Student</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Class</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Membership</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Payment</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-800">
                  {registrations.map(reg => {
                    const regClass = classes.find(c => c.id === reg.class_id);
                    const payStatus = reg.payment_status || 'unknown';
                    const canRefund = payStatus === 'paid';
                    return (
                      <tr key={reg.id} className="hover:bg-stone-800/50">
                        <td className="px-4 py-3">
                          <p className="font-medium">{reg.first_name} {reg.last_name}</p>
                          <p className="text-sm text-stone-400">{reg.email}</p>
                          {reg.phone && <p className="text-sm text-stone-500">{reg.phone}</p>}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {regClass?.name || 'Unknown Class'}
                        </td>
                        <td className="px-4 py-3 text-sm capitalize">
                          {reg.membership_type?.replace('_', ' ')}
                        </td>
                        <td className="px-4 py-3 text-sm text-stone-400">
                          {new Date(reg.registered_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs capitalize ${
                            payStatus === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                            payStatus === 'refunded' ? 'bg-orange-500/20 text-orange-400' :
                            payStatus === 'free' ? 'bg-sky-500/20 text-sky-400' :
                            'bg-stone-700 text-stone-300'
                          }`}>
                            {payStatus}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            reg.status === 'active' ? 'bg-green-500/20 text-green-400' :
                            reg.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-stone-700 text-stone-300'
                          }`}>
                            {reg.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {reg.status !== 'active' && (
                              <button
                                onClick={() => handleUpdateRegistrationStatus(reg.id, 'active')}
                                className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                                title="Mark Active"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            {reg.status !== 'cancelled' && (
                              <button
                                onClick={() => handleUpdateRegistrationStatus(reg.id, 'cancelled')}
                                className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                                title="Cancel"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                            {canRefund && (
                              <button
                                onClick={() => handleRefundRegistration(reg)}
                                className="p-1 hover:bg-orange-500/20 text-orange-400 rounded"
                                title="Refund Stripe charge"
                                data-testid={`refund-reg-${reg.id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </button>
                            )}
                            <a href={`mailto:${reg.email}`} className="p-1 hover:bg-stone-700 rounded">
                              <Mail className="w-4 h-4" />
                            </a>
                            {reg.phone && (
                              <a href={`tel:${reg.phone}`} className="p-1 hover:bg-stone-700 rounded">
                                <Phone className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Private Lessons Tab */}
        {activeTab === 'private' && (
          <div className="bg-stone-900 border border-stone-800 rounded-xl overflow-hidden">
            {privateLessons.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="w-12 h-12 text-stone-600 mx-auto mb-4" />
                <p className="text-stone-400">No private lesson requests yet</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-stone-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Student</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Lesson Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Preferred Time</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Group Size</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-stone-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-800">
                  {privateLessons.map(req => (
                    <tr key={req.id} className="hover:bg-stone-800/50">
                      <td className="px-4 py-3">
                        <p className="font-medium">{req.first_name} {req.last_name}</p>
                        <p className="text-sm text-stone-400">{req.email}</p>
                        <p className="text-sm text-stone-500">{req.phone}</p>
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">
                        {req.lesson_type?.replace('_', ' ')}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {req.preferred_date && req.preferred_time ? (
                          <>
                            {req.preferred_date}<br/>
                            {req.preferred_time}
                          </>
                        ) : (
                          <span className="text-stone-500">Flexible</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {req.group_size} {req.group_size > 1 ? 'people' : 'person'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          req.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          req.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                          req.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-amber-500/20 text-amber-400'
                        }`}>
                          {req.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <select
                            value={req.status}
                            onChange={(e) => handleUpdatePrivateLessonStatus(req.id, e.target.value)}
                            className="bg-stone-800 border border-stone-700 rounded px-2 py-1 text-sm"
                          >
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="completed">Completed</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                          <a href={`mailto:${req.email}`} className="p-1 hover:bg-stone-700 rounded">
                            <Mail className="w-4 h-4" />
                          </a>
                          <a href={`tel:${req.phone}`} className="p-1 hover:bg-stone-700 rounded">
                            <Phone className="w-4 h-4" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Class Modal */}
      {showClassModal && (
        <ClassModal
          danceClass={editingClass}
          onClose={() => { setShowClassModal(false); setEditingClass(null); }}
          onSave={handleSaveClass}
        />
      )}
    </div>
  );
};

const ClassModal = ({ danceClass, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: danceClass?.name || '',
    description: danceClass?.description || '',
    instructor: danceClass?.instructor || 'Mestre Curtis Pierre',
    day_of_week: danceClass?.day_of_week || 'Monday',
    start_time: danceClass?.start_time || '7:00 PM',
    end_time: danceClass?.end_time || '9:00 PM',
    level: danceClass?.level || 'all',
    category: danceClass?.category || 'adult',
    location: danceClass?.location || '800 Race Street, New Orleans, LA 70130',
    price_per_class: danceClass?.price_per_class || 10,
    price_monthly: danceClass?.price_monthly || 45,
    price_yearly: danceClass?.price_yearly || 150,
    max_capacity: danceClass?.max_capacity || '',
    is_free: danceClass?.is_free || false,
    notes: danceClass?.notes || '',
    is_active: danceClass?.is_active !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price_per_class: parseFloat(formData.price_per_class),
      price_monthly: parseFloat(formData.price_monthly),
      price_yearly: parseFloat(formData.price_yearly),
      max_capacity: formData.max_capacity ? parseInt(formData.max_capacity) : null
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-stone-900 border border-stone-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-6 border-b border-stone-800">
          <h2 className="text-xl font-bold">{danceClass ? 'Edit Class' : 'Create New Class'}</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Class Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg focus:ring-2 focus:ring-amber-500"
              placeholder="e.g., Advanced Samba Class"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Day of Week *</label>
              <select
                value={formData.day_of_week}
                onChange={e => setFormData({ ...formData, day_of_week: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
              >
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Instructor</label>
              <input
                type="text"
                value={formData.instructor}
                onChange={e => setFormData({ ...formData, instructor: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Time</label>
              <input
                type="text"
                value={formData.start_time}
                onChange={e => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                placeholder="7:00 PM"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Time</label>
              <input
                type="text"
                value={formData.end_time}
                onChange={e => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                placeholder="9:00 PM"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Category</label>
              <select
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
              >
                <option value="adult">Adult</option>
                <option value="kids">Kids</option>
                <option value="private">Private</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Level</label>
              <select
                value={formData.level}
                onChange={e => setFormData({ ...formData, level: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
              >
                <option value="all">All Levels</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Max Capacity</label>
              <input
                type="number"
                value={formData.max_capacity}
                onChange={e => setFormData({ ...formData, max_capacity: e.target.value })}
                className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                placeholder="Unlimited"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Location</label>
            <input
              type="text"
              value={formData.location}
              onChange={e => setFormData({ ...formData, location: e.target.value })}
              className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
            />
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_free}
                onChange={e => setFormData({ ...formData, is_free: e.target.checked })}
                className="w-4 h-4 rounded border-stone-600"
              />
              <span className="text-sm">Free Class</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 rounded border-stone-600"
              />
              <span className="text-sm">Active (visible on website)</span>
            </label>
          </div>

          {!formData.is_free && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Price Per Class ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_class}
                  onChange={e => setFormData({ ...formData, price_per_class: e.target.value })}
                  className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Monthly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_monthly}
                  onChange={e => setFormData({ ...formData, price_monthly: e.target.value })}
                  className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Yearly Price ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_yearly}
                  onChange={e => setFormData({ ...formData, price_yearly: e.target.value })}
                  className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Notes (shown on class card)</label>
            <input
              type="text"
              value={formData.notes}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-4 py-2 bg-stone-800 border border-stone-700 rounded-lg"
              placeholder="e.g., Your first class is free!"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 border border-stone-700 rounded-lg font-bold hover:bg-stone-800 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 py-3 bg-amber-500 text-stone-900 rounded-lg font-bold hover:bg-amber-400 transition-colors"
            >
              {danceClass ? 'Update Class' : 'Create Class'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default AdminClasses;
